import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkPlus, Clock, Trash2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import { KIND_LABEL_KEY, isSatKind } from '../../constants';
import type { AuthorSection, IeltsWritingTask, UpdateExamSectionInput } from '../../types';
import { AudioTracksEditor } from './AudioTracksEditor';
import { SlotSourceEditor } from './SlotSourceEditor';
import { ExamQuestionList } from './ExamQuestionList';
import type { ExamQuestionPayload } from './ExamQuestionDialog';

interface Props {
  testId: string;
  section: AuthorSection;
  /** SAT module 2 variants share their subject's source; only module 1 shows the picker. */
  showSourceEditor: boolean;
  locked: boolean;
  onUpdate: (data: UpdateExamSectionInput) => Promise<void>;
  onAddQuestion: (payload: ExamQuestionPayload) => Promise<void>;
  onUpdateQuestion: (questionId: string, payload: ExamQuestionPayload) => Promise<void>;
  onDeleteQuestion: (questionId: string) => Promise<void>;
  onSaveToLibrary: () => Promise<void>;
  /** Only the optional Speaking section can be removed, and only while it is empty. */
  onRemove?: () => Promise<void>;
  issues: string[];
  /** False on a single-part test: library blocks are whole sections, so it neither draws from nor saves to the library. */
  libraryEnabled?: boolean;
  /** A Writing test about one task: its question is always that task. */
  fixedWritingTask?: IeltsWritingTask;
}

const TIER_KEY: Record<string, string> = { BASE: 'exam.sat.module1', EASY: 'exam.sat.module2_easy', HARD: 'exam.sat.module2_hard' };

/** One exam section on the builder: timer, content source, audio and questions. */
export function ExamSectionCard({
  testId,
  section,
  showSourceEditor,
  locked,
  onUpdate,
  onAddQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  onSaveToLibrary,
  onRemove,
  issues,
  libraryEnabled = true,
  fixedWritingTask,
}: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [minutes, setMinutes] = useState(section.timeLimitSeconds ? String(Math.round(section.timeLimitSeconds / 60)) : '');
  const fromLibrary = section.source?.mode === 'RANDOM' || Boolean(section.source?.blockId);

  useEffect(() => {
    setMinutes(section.timeLimitSeconds ? String(Math.round(section.timeLimitSeconds / 60)) : '');
  }, [section.timeLimitSeconds]);

  const saveTimer = async () => {
    const n = Number(minutes);
    const next = minutes.trim() === '' ? null : Math.round(n * 60);
    if (next !== null && (!Number.isFinite(n) || n < 1 || n > 240)) {
      toast.error(t('exam.timer_range'));
      return;
    }
    if (next === (section.timeLimitSeconds ?? null)) return;
    await onUpdate({ timeLimitSeconds: next });
  };

  const run = async (fn: () => Promise<void>, ok?: string) => {
    try {
      await fn();
      if (ok) toast.success(ok);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('failed_to_save'));
    }
  };

  return (
    <section
      aria-labelledby={`sec-${section.id}`}
      className={cn('rounded-xl border bg-card p-4 sm:p-5', issues.length ? 'border-warning-400' : 'border-border')}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t(KIND_LABEL_KEY[section.kind ?? 'GENERIC'])}
            {section.moduleTier && ` · ${t(TIER_KEY[section.moduleTier])}`}
          </p>
          <h3 id={`sec-${section.id}`} className="font-display text-lg font-bold text-foreground">
            {section.title}
          </h3>
        </div>
        <div className="flex items-end gap-2">
          <label className="space-y-1 text-xs">
            <span className="flex items-center gap-1 font-medium text-muted-foreground">
              <Clock className="h-3.5 w-3.5" aria-hidden /> {t('exam.minutes')}
            </span>
            <Input
              type="number"
              min={1}
              max={240}
              value={minutes}
              disabled={locked}
              onChange={(e) => setMinutes(e.target.value)}
              onBlur={() => run(saveTimer)}
              className="h-9 w-24"
              aria-label={t('exam.section_minutes', { section: section.title })}
            />
          </label>
          {libraryEnabled && !fromLibrary && section.kind && section.kind !== 'GENERIC' && (!isSatKind(section.kind) || section.moduleTier === 'BASE') && (
            <Button
              size="sm"
              variant="outline"
              disabled={!section.questions.length}
              onClick={() => run(onSaveToLibrary, t('exam.library.saved'))}
              className="gap-1.5"
              title={t('exam.library.save_help')}
            >
              <BookmarkPlus className="h-4 w-4" aria-hidden /> {t('exam.library.save')}
            </Button>
          )}
          {onRemove && (
            <Button
              size="sm"
              variant="outline"
              disabled={locked || section.questions.length > 0}
              onClick={() => run(onRemove, t('exam.speaking.removed'))}
              className="gap-1.5"
              title={t('exam.speaking.remove_help')}
            >
              <Trash2 className="h-4 w-4" aria-hidden /> {t('exam.speaking.remove')}
            </Button>
          )}
        </div>
      </div>

      {issues.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm text-warning-700">
          {issues.map((i) => (
            <li key={i}>• {i}</li>
          ))}
        </ul>
      )}

      <div className="mt-4 space-y-5">
        {showSourceEditor && libraryEnabled && (
          <div>
            <p className="mb-2 text-sm font-semibold text-foreground">{t('exam.source.title')}</p>
            <SlotSourceEditor
              kind={section.kind ?? 'GENERIC'}
              value={section.source}
              disabled={locked}
              onSave={(source) => run(() => onUpdate({ source }), t('saved'))}
            />
          </div>
        )}

        {fromLibrary ? (
          <p className="rounded-lg bg-muted px-3 py-2.5 text-sm text-muted-foreground">
            {section.source?.mode === 'RANDOM' ? t('exam.source.random_note') : t('exam.source.pinned_note')}
          </p>
        ) : (
          <>
            {section.kind === 'IELTS_LISTENING' && (
              <AudioTracksEditor
                value={section.stimulus}
                disabled={locked}
                questionCount={section.questions.length}
                onChange={(stimulus) => run(() => onUpdate({ stimulus }))}
              />
            )}
            <ExamQuestionList
              kind={section.kind ?? 'GENERIC'}
              questions={section.questions}
              uploadContext={`tests/${testId}`}
              fixedWritingTask={fixedWritingTask}
              disabled={locked}
              onAdd={onAddQuestion}
              onUpdate={onUpdateQuestion}
              onDelete={onDeleteQuestion}
            />
          </>
        )}
      </div>
    </section>
  );
}

export default ExamSectionCard;
